import React, { useState, useEffect, useCallback } from "react";
import { Download } from "lucide-react";
import { api, fetchBlob } from "../api/client";
import LoadingSpinner from "../components/LoadingSpinner";

const TABS = [
    { key: "state", label: "By State" },
    { key: "rpl", label: "By RPL" },
    { key: "site", label: "By Site" },
    { key: "findings", label: "Findings by Site" },
];

function currentMonth() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function Total({ rows, col }) {
    const sum = (rows || []).reduce((a, r) => a + (Number(r[col]) || 0), 0);
    return <span style={{ color: "var(--text-muted)" }}>Total {col}: <b>{sum}</b></span>;
}

function ReportsSummary() {
    const [month, setMonth] = useState(currentMonth());
    const [tab, setTab] = useState("state");
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [downloading, setDownloading] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            let data;
            if (tab === "findings") {
                data = await api.reportsFindings(month);
            } else {
                data = await api.reportsMtdSummary(tab, month);
            }
            setRows(data.rows || []);
        } catch (err) {
            setError(err.message);
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [tab, month]);

    useEffect(() => { load(); }, [load]);

    const onDownload = async () => {
        setDownloading(true);
        try {
            const blob = await fetchBlob(`/reports/export.xlsx?month=${encodeURIComponent(month)}`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `MtdAuditReport_${month}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err) {
            setError("Download failed: " + err.message);
        } finally {
            setDownloading(false);
        }
    };

    let columns;
    if (tab === "state") columns = ["Date", "State", "Opened", "Actioned", "Closed", "Outstanding"];
    else if (tab === "rpl") columns = ["Date", "RPL", "Opened", "Actioned", "Closed", "Outstanding"];
    else if (tab === "site") columns = ["Date", "SiteId", "SiteName", "State", "RPL", "Opened", "Actioned", "Closed", "Outstanding"];
    else columns = ["Date", "SiteId", "SiteName", "State", "Category", "Severity", "Title", "Comment"];

    const numericCols = new Set(["Opened", "Actioned", "Closed", "Outstanding", "Count"]);

    return (
        <div className="page-container">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0 }}>Reports</h1>
                    <p style={{ margin: "4px 0 0", color: "var(--text-muted)" }}>
                        MTD audit summary. Counts are events per day: Opened (first detected), Actioned (open &rarr; verify), Closed (resolved).
                    </p>
                </div>
                <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <label style={{ display: "flex", flexDirection: "column", fontSize: 12, color: "var(--text-muted)" }}>
                        Month
                        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                            style={{ padding: 6, borderRadius: 6, border: "1px solid var(--border)", marginTop: 2 }} />
                    </label>
                    <button onClick={onDownload} disabled={downloading}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px",
                            background: "var(--accent, #2563eb)", color: "white", border: "none",
                            borderRadius: 6, cursor: downloading ? "wait" : "pointer", opacity: downloading ? 0.7 : 1 }}>
                        <Download size={16} />
                        {downloading ? "Preparing..." : "Download xlsx"}
                    </button>
                </div>
            </div>

            <div style={{ display: "flex", gap: 4, borderBottom: "1px solid var(--border)", marginBottom: 12 }}>
                {TABS.map((t) => (
                    <button key={t.key} onClick={() => setTab(t.key)}
                        style={{
                            padding: "8px 16px", border: "none", background: "none",
                            borderBottom: tab === t.key ? "2px solid var(--accent, #2563eb)" : "2px solid transparent",
                            color: tab === t.key ? "var(--accent, #2563eb)" : "var(--text)",
                            cursor: "pointer", fontWeight: tab === t.key ? 600 : 400,
                        }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {error && (
                <div style={{ padding: 12, background: "#fee", color: "#c00", borderRadius: 6, marginBottom: 12 }}>
                    {error}
                </div>
            )}

            {loading ? (
                <LoadingSpinner message="Loading..." />
            ) : rows.length === 0 ? (
                <div style={{ padding: 24, textAlign: "center", color: "var(--text-muted)" }}>
                    No data for {month}.
                </div>
            ) : (
                <>
                    <div style={{ marginBottom: 8, fontSize: 12, color: "var(--text-muted)" }}>
                        {rows.length} rows{tab !== "findings" && (
                            <>
                                {" "}&middot; <Total rows={rows} col="Opened" />
                                {" "}&middot; <Total rows={rows} col="Actioned" />
                                {" "}&middot; <Total rows={rows} col="Closed" />
                            </>
                        )}
                    </div>
                    <div style={{ overflow: "auto", border: "1px solid var(--border)", borderRadius: 6 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                            <thead style={{ background: "var(--bg-muted, #f5f5f5)", position: "sticky", top: 0 }}>
                                <tr>
                                    {columns.map((c) => (
                                        <th key={c} style={{ textAlign: numericCols.has(c) ? "right" : "left", padding: "8px 12px", borderBottom: "1px solid var(--border)" }}>
                                            {c}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((r, i) => (
                                    <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                                        {columns.map((c) => (
                                            <td key={c} style={{ padding: "6px 12px", textAlign: numericCols.has(c) ? "right" : "left" }}>
                                                {r[c] == null ? "" : String(r[c])}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
}

export default ReportsSummary;
